// 在庫不足フォームのウィジェット（ChatBan #47）。
//
// 置き方:
//   <div data-wu-widget="stock-shortage" data-site="1"
//        data-product-code="12345" data-product-name="OPP袋テープ付 A4用 標準#30"></div>
//
// 商品は URL のクエリでも受けられる（Issue #22。商品ページ → 案内ページの導線、ChatBan #151）:
//   <div data-wu-widget="stock-shortage" data-site="1"></div>   ← ページ URL が ?p=200
//   data-product-code が無ければ ?p= を管理番号にし、商品名はサーバー側の API が同期テーブルだけで引く（Issue #95）。
//   data-product-code / data-product-name があるときは従来どおりそちらが勝つ。
//   引けなければ何も描画しない（客にエラーを見せない）。決め方は product-code.ts。
//   ?soldout=1 が付いていたら在庫切れの商品ページから来たもの（hooks/stock-check.ts の
//   「予約購入/入荷通知はこちら →」）。①「在庫表示分だけ納品」が成り立たないので②③の2択にし、在庫も引かない。
//
// 入口は「ご注文方法のご案内」の3択カード（Issue #20）。フォームは常にカードの下に出ていて（Issue #24）、
// ②予約 / ③入荷通知 のカードは種別の選択に使う（押すとラジオが変わり、カードが強調される）。
// 種別を選ぶまで送信ボタンは無効。**在庫が足りないときにだけ置く**（出し分けはページ側の責任）。
// 在庫表示ウィジェット（移設対象3）が入ったら、そちらから出し分けられるように
// window.wuStockShortage.show()/hide() を出してある。
//
// ★登録完了画面は DB から読み直さない。SELECT 権限が無いので読めない（読む必要も無い）。
//   出すのは手元の入力値だけ。

import { QUANTITY_MAX } from '../../shared/stock-shortage-limits'
import { injectStyleOnce } from '../../shared/style'
import type { Widget, WidgetContext } from '../../shared/registry'
import { fetchProductName, postEntry } from './api'
import { resolveProductCode } from './product-code'
import { readStockFromDom, fetchDisplayStock } from './stock'
import { CSS } from './style'
import { doneHtml, html } from './template'
import { TEXT as T } from './text'

/** 既定の叩き先。★絶対URLで書くこと（店のページから見れば別オリジン。相対だと店を叩いて404）。 */
const DEFAULT_API = 'https://front.workup-system.com/api/stock-shortage'

const KIND_RESERVE = 1
const KIND_NOTIFY = 2
/** 在庫切れの商品ページから来た印（hooks/stock-check.ts と合わせる） */
export const SOLD_OUT_PARAM = 'soldout'

const widget: Widget = (el: HTMLElement, ctx: WidgetContext): void => {
  const api = el.dataset.api?.trim() || DEFAULT_API

  // 商品が分からなければ受け付けても業務側で紐付けられない。黙って出さない方が安全。
  const resolved = resolveProductCode(el.dataset, location.search)
  if (resolved === null) {
    console.warn('[wu] stock-shortage: data-product-code か ?p= の商品管理番号が必要です', el.dataset)
    return
  }
  const productCode = resolved.code
  const soldOut = new URLSearchParams(location.search).get(SOLD_OUT_PARAM) === '1'

  // 商品名: data-product-name があればそれ。無ければ API から引く（クエリ経路の通常形）。
  const attrName = (el.dataset.productName ?? '').trim()
  if (attrName !== '') {
    render(el, ctx, api, productCode, attrName, soldOut)
    return
  }
  fetchProductName(api, ctx.siteId, productCode).then((name) => {
    if (name === null) {
      console.warn(`[wu] stock-shortage: 商品名を引けないため描画しません (site=${ctx.siteId}, code=${productCode})`)
      return
    }
    render(el, ctx, api, productCode, name, soldOut)
  })
}

function render(el: HTMLElement, ctx: WidgetContext, api: string, productCode: number, productName: string, soldOut: boolean): void {
  injectStyleOnce('stock-shortage', CSS)
  el.classList.add('zkform')
  el.innerHTML = html(productName, productCode, { soldOut })

  const q = <E extends HTMLElement>(cls: string): E => el.querySelector<E>('.' + cls)!
  const form = q<HTMLFormElement>('js-form')
  const submit = q<HTMLButtonElement>('js-submit')
  const inline = q('js-inline')
  const done = q('js-done')
  const qtyLabel = q('js-qtylabel')
  const picks = Array.from(el.querySelectorAll<HTMLElement>('.js-pick'))

  // 表示在庫（.stockInfo と同じ数字）を引けたときだけ、選択肢1の下に静かに足す。
  // 商品ページなら DOM の .stockInfo から（ProductDetails.js が差し込むまで少し待つ）、
  // フォーム単独のページ（page/2522 等）なら StockCheck を直接取得する。
  // 引けない商品は普通にあるので、失敗しても何も出さない（エラー表示・「確認中」は禁止）。
  if (!soldOut) void (async () => {
    let stock = readStockFromDom()
    // .stockInfo が差し込まれるのは商品ページ（/product/N）だけ。そこでは別のフックが非同期に
    // 差し込むので少しだけ待って再確認する。それ以外のページ（page/2522 等）で待つと API に行くまで
    // 3 秒遅れるだけなので待たない（2026-09-10 実測: 0.8 秒 → 3.9 秒になっていた）。
    const onProductPage = /^\/product\/\d+\/?$/.test(location.pathname)
    if (stock == null && onProductPage) {
      for (let i = 0; i < 5 && stock == null; i++) {
        await new Promise((res) => setTimeout(res, 600))
        stock = readStockFromDom()
      }
    }
    stock ??= await fetchDisplayStock(ctx.siteId, productCode)
    if (stock == null) return
    const slot = el.querySelector<HTMLElement>('.js-stock')
    if (slot == null) return
    slot.textContent = T.stockLine(stock.num, stock.unit)
    slot.hidden = false
    const back = el.querySelector<HTMLElement>('.js-back')
    if (back) back.hidden = false
  })()

  const field = (name: string): HTMLInputElement | HTMLTextAreaElement | null =>
    form.querySelector(`[name="${name}"]`)
  const val = (name: string): string => field(name)?.value.trim() ?? ''
  const radios = Array.from(form.querySelectorAll<HTMLInputElement>('input[name="kind"]'))
  const kind = (): number => Number(radios.find((r) => r.checked)?.value ?? 0)

  // 予約注文は数量が必須、入荷通知は任意（DB の CK_..._予約注文必須 と同じ条件）。
  // 必須の見え方を選択に追従させる。カードの選択状態（is-picked / aria-pressed）と
  // 送信ボタンの有効・無効（種別未選択なら無効。Issue #24）もここで揃える。
  // ラジオを直接変えてもカードを押しても、ここを通るので見た目が食い違わない。
  function syncKind(): void {
    const k = kind()
    qtyLabel.innerHTML =
      k === KIND_RESERVE ? `${T.label.quantity}<span class="req">${T.label.required}</span>` : T.label.quantityOptional
    picks.forEach((p) => {
      const on = Number(p.dataset.kind) === k
      p.classList.toggle('is-picked', on)
      p.setAttribute('aria-pressed', on ? 'true' : 'false')
    })
    submit.disabled = k !== KIND_RESERVE && k !== KIND_NOTIFY
  }
  radios.forEach((r) => r.addEventListener('change', syncKind))
  syncKind()

  // 3択カード。②③は種別の選択に使う（Issue #24）。フォームは最初から出ているので開く処理は無い。
  // ①はフォーム不要なので押せない（js-pick を持たない）。
  function pick(p: HTMLElement): void {
    const k = Number(p.dataset.kind)
    const r = radios.find((r) => Number(r.value) === k)
    if (!r) return
    r.checked = true
    syncKind()
  }
  picks.forEach((p) => {
    p.addEventListener('click', () => pick(p))
    // role="button" tabindex="0" なので Enter / Space でも押せるようにする（元 HTML の作法を維持）
    p.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault()
        pick(p)
      }
    })
  })

  function showError(text: string): void {
    done.className = 'msg'
    done.textContent = ''
    inline.className = 'msg err'
    inline.textContent = text
    try {
      inline.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } catch {
      /* 古いブラウザは黙って無視。表示自体は済んでいる */
    }
  }

  // 送信中の二重送信を止める（ボタンは disabled にするが、submit イベント自体はそれに依らず来る）
  let sending = false
  form.addEventListener('submit', (ev) => {
    ev.preventDefault()
    if (sending) return

    const k = kind()
    if (k !== KIND_RESERVE && k !== KIND_NOTIFY) return showError(T.error.kind)

    // 数量は空欄なら null。**0 や空文字を送らない**（DB の CHECK 違反になる）。
    const raw = val('quantity').replace(/[,，\s]/g, '')
    let quantity: number | null = null
    if (raw !== '') {
      const n = Number(raw)
      if (!Number.isInteger(n) || n <= 0) return showError(T.error.quantityPositive)
      // 上限超えはサーバーも 400 で返すが、入力を残したまま数量だけ直せるよう手前で止める（Issue #18）
      if (n > QUANTITY_MAX) return showError(T.error.quantityMax(QUANTITY_MAX.toLocaleString('ja-JP')))
      quantity = n
    }
    if (k === KIND_RESERVE && quantity === null) return showError(T.error.quantity)

    const name = val('name')
    if (!name) return showError(T.error.name)

    const email = val('email')
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return showError(T.error.email)

    sending = true
    submit.disabled = true
    submit.textContent = T.submitting
    inline.className = 'msg'
    inline.textContent = ''

    postEntry(api, {
      kind: k,
      siteInfoId: ctx.siteId,
      productCode,
      productName,
      quantity,
      name,
      email,
      tel: val('tel'),
      company: val('company'),
      note: val('note'),
      website: val('website'),
    })
      .then((ok) => {
        if (!ok) {
          sending = false
          submit.disabled = false
          submit.textContent = T.submit
          showError(T.error.failed)
          return
        }
        // 受け付けた内容は手元の値だけで出す（DBは読み返せない）。
        // ★ !important 付きで隠す。パックジャパンは hooks/stock-shortage-page.ts が `.zkform form{display:block!important}`
        //   （テーマが freepage 内の form を隠す対策）を入れており、素の inline style では負けてフォームが残る
        //   （2026-09-22 田口さん報告: 「送信中…」のまま緑の受付文が下に出る）。旧 /page/694 も setProperty(..., 'important') だった
        form.style.setProperty('display', 'none', 'important')
        inline.className = 'msg'
        inline.textContent = ''
        done.className = 'msg js-done ok zk-done'
        done.setAttribute('role', 'status')
        done.innerHTML = doneHtml({
          kindLabel: k === KIND_RESERVE ? T.label.kindReserve : T.label.kindNotify,
          productName,
          quantity,
        })
        try {
          done.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } catch {
          /* 同上 */
        }
      })
      .catch(() => {
        sending = false
        submit.disabled = false
        submit.textContent = T.submit
        showError(T.error.network)
      })
  })
}

export default widget
